// BUSCA POR IA (modelo_busca = 4): query pública `buscaIa(texto)` do site-graphql.
// Vai pelo MESMO caminho GraphQL público do resto do front (/api/gql no client).
// O backend nunca falha "feio" (sem IA configurada devolve fonte:"fallback"), mas
// aqui ainda assim degradamos pra null em erro de rede — o hero NUNCA trava.
import { gql, GraphQLClient } from 'graphql-request';
import { BASE_URL, headers } from '../configs/constants';

// subset dos args de imoveis_busca devolvido pela IA (JSON — pode vir string ou objeto)
export interface BuscaIaFilters {
    id_cidade?: number;
    id_bairro?: number[];
    id_tipo?: number[];
    venda?: number;
    aluguel?: number;
    temporada?: number;
    preco_venda_min?: number;
    preco_venda_max?: number;
    preco_locacao_min?: number;
    preco_locacao_max?: number;
    dormitorios_min?: number;
    suites_min?: number;
    banheiros_min?: number;
    garagems_min?: number;
    metragem_min?: number;
    metragem_max?: number;
    referencia?: string[];
}

// Pergunta de desambiguação (número que é código E valor plausível).
export interface BuscaIaDisambiguation {
    pergunta: string;
    opcoes: Array<{ label: string; filters: BuscaIaFilters }>;
}

export interface BuscaIaResult {
    filters: BuscaIaFilters;
    interpretacao: string;
    fonte: 'ia' | 'fallback' | string;
    desambiguacao?: BuscaIaDisambiguation | null;
    // ── v2 (INC-0018): só adições. Backend sem v2 (ou a query antiga, no fallback) = ausentes.
    versao?: string | null;
    /** "Não encontramos studio no Paraíso — mostrando os mais próximos (…)" */
    aviso?: string | null;
    n_resultados?: number | null;
    termos_ignorados?: string[] | null;
    /** mesmo bairro em outras cidades do site ("Centro de outra cidade?") */
    outras_cidades?: Array<{ id_cidade: number; nome: string; id_bairro: number[]; n: number }> | null;
    lugar?: { termo: string; relacao?: string | null; resolucao: string; rotulo?: string | null } | null;
    /** centro do "perto de…" (ordena a listagem por distância) */
    ponto_referencia?: { lat: number; lng: number; rotulo?: string | null; fonte?: string | null } | null;
}

// `desambiguacao` também é scalar JSON — pode chegar objeto ou string.
export function parseBuscaIaDesambiguacao(raw: unknown): BuscaIaDisambiguation | null {
    let obj: any = raw;
    if (typeof raw === 'string') {
        try {
            obj = JSON.parse(raw);
        } catch {
            return null;
        }
    }
    if (!obj || typeof obj !== 'object' || !obj.pergunta || !Array.isArray(obj.opcoes)) return null;
    const opcoes = obj.opcoes
        .filter((o: any) => o && o.label)
        .map((o: any) => ({ label: String(o.label), filters: parseBuscaIaFilters(o.filters) }));
    return opcoes.length ? { pergunta: String(obj.pergunta), opcoes } : null;
}

// `filters` é um scalar JSON: dependendo do serializer chega como objeto ou como string.
export function parseBuscaIaFilters(raw: unknown): BuscaIaFilters {
    if (!raw) return {};
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof raw === 'object' ? (raw as BuscaIaFilters) : {};
}

export async function buscaIa(
    texto: string,
    origin?: string,
    codsite?: number
): Promise<BuscaIaResult | null> {
    if (!texto || (!origin && typeof window === 'undefined')) {
        return null;
    }

    // client-side sempre bate no BFF /api/gql (mesmo caminho das buscas normais)
    const endpoint = typeof window !== 'undefined' ? '/api/gql' : BASE_URL;

    const graphQLClient = new GraphQLClient(endpoint, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
            query: 'busca_ia',
        },
    });

    // texto vai por VARIABLE (não interpolado): frase livre do usuário — evita quebrar a
    // query com aspas/quebras de linha. O BFF não cacheia o buscaIa (o site-graphql tem cache
    // próprio de 15 min + log), ver pages/api/gql.ts.
    //
    // v2 (INC-0018 F4): `v: 2` diz ao backend que este front entende o contrato novo e pede os
    // campos novos (aviso, ponto do "perto", outras cidades…). Quem liga a v2 é o gate do backend.
    const queryV2 = gql`
        query BuscaIa($texto: String!, $v: Int) {
            buscaIa(texto: $texto, v: $v) {
                filters
                interpretacao
                fonte
                desambiguacao
                versao
                aviso
                n_resultados
                termos_ignorados
                outras_cidades {
                    id_cidade
                    nome
                    id_bairro
                    n
                }
                lugar {
                    termo
                    relacao
                    resolucao
                    rotulo
                }
                ponto_referencia {
                    lat
                    lng
                    rotulo
                    fonte
                }
            }
        }
    `;
    // a query de antes, byte a byte: backend sem a v2 (ou o Worker de fallback do BFF)
    const queryV1 = gql`
        query BuscaIa($texto: String!) {
            buscaIa(texto: $texto) {
                filters
                interpretacao
                fonte
                desambiguacao
            }
        }
    `;

    try {
        const data: any = await graphQLClient.request(queryV2, { texto, v: 2 });
        return data?.buscaIa || null;
    } catch (error: any) {
        // mesmo padrão de resiliência de services/properties.ts: aproveita dados parciais
        const partial = error?.response?.data;
        if (partial?.buscaIa) return partial.buscaIa;
        // schema sem os campos/argumento da v2 → refaz com a query antiga (compatibilidade)
        if (!erroDeSchemaV2(error)) return null;
    }

    try {
        const data: any = await graphQLClient.request(queryV1, { texto });
        return data?.buscaIa || null;
    } catch (error: any) {
        const partial = error?.response?.data;
        if (partial?.buscaIa) return partial.buscaIa;
        return null;
    }
}

// Erro de VALIDAÇÃO do GraphQL por causa da v2 (campo/argumento/tipo que o servidor não conhece).
function erroDeSchemaV2(error: any): boolean {
    const mensagens: string[] = Array.isArray(error?.response?.errors)
        ? error.response.errors.map((e: any) => String(e?.message ?? ''))
        : [String(error?.message ?? '')];
    return mensagens.some((m) => /Cannot query field|Unknown argument|Unknown type|Variable "\$v"/i.test(m));
}

// Cidade de um bairro (a busca por IA pode devolver `id_bairro` sem `id_cidade`; a listagem só
// casa bairro DENTRO da cidade). 0 = não achou — quem chama segue sem o bairro, como antes.
export async function cidadeDoBairro(idBairro: number, origin?: string, codsite?: number): Promise<number> {
    const id = Number(idBairro);
    if (!Number.isFinite(id) || id <= 0 || (!origin && typeof window === 'undefined')) return 0;

    const graphQLClient = new GraphQLClient(BASE_URL, {
        headers: {
            ...headers,
            dominion: typeof window !== 'undefined' ? window.location.origin : origin,
            'x-graphql-client-name': typeof window !== 'undefined' ? window.location.origin : origin,
            codsite: codsite ? codsite : 0,
        },
    });
    const query = gql`
        query BairroCidade($id: Int) {
            bairros(id_bairro: $id) {
                id
                id_cidade
            }
        }
    `;
    try {
        const data: any = await graphQLClient.request(query, { id: Math.floor(id) });
        const cidade = Number(data?.bairros?.[0]?.id_cidade);
        return Number.isFinite(cidade) && cidade > 0 ? cidade : 0;
    } catch {
        return 0;
    }
}
